import React, { useState, useCallback } from 'react';
import { List, RefreshCw } from 'lucide-react';
import { claimsService, LeadClaim } from '@/services/claimsService';
import { useUser } from '@/contexts/UserContext';
import { showToast } from '@/components/ui/Toast';
import ClaimsTable from '../_Components/ClaimsTable';

type StatusFilter = 'all' | 'pending' | 'approved' | 'declined';

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = new Date();
const thirtyDaysAgo = new Date(today);
thirtyDaysAgo.setDate(today.getDate() - 30);

const AllClaims: React.FC = () => {
  const { userData } = useUser();
  const isSuperAdmin = userData?.data?.role?.role_id === 9401;

  const [claims, setClaims] = useState<LeadClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [fromDate, setFromDate] = useState(toISO(thirtyDaysAgo));
  const [toDate, setToDate] = useState(toISO(today));

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await claimsService.getAllClaims(fromDate, toDate);
      if (res.success) setClaims(res.data || []);
    } catch {
      showToast('Failed to fetch claims', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const filtered = filterStatus === 'all' ? claims : claims.filter(c => c.status === filterStatus);

  return (
    <div className="flex flex-col h-full">
      {/* Fixed header with date filters */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-3 shrink-0">
          <List className="h-5 w-5 text-brandColor" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">All Claims</h1>
            <p className="text-xs text-gray-400">All claim requests across all agents</p>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Period:</span>
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
          />
          <span className="text-gray-400 text-sm">–</span>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
          />
          <button
            onClick={fetchClaims}
            disabled={loading}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-brandColor text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 whitespace-nowrap"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Apply
          </button>
        </div>
      </div>

      {/* Fixed status filter bar */}
      <div className="bg-white border-b px-6 py-3 flex items-center gap-2 shrink-0">
        <span className="text-xs text-gray-500 font-medium mr-1">Filter:</span>
        {(['all', 'pending', 'approved', 'declined'] as StatusFilter[]).map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors capitalize ${
              filterStatus === s
                ? 'bg-brandColor text-white border-brandColor'
                : 'text-gray-600 border-gray-300 hover:border-brandColor'
            }`}
          >
            {s}
          </button>
        ))}
        {claims.length > 0 && (
          <span className="ml-auto text-xs text-gray-400">{filtered.length} of {claims.length} records</span>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin h-8 w-8 border-2 border-brandColor border-t-transparent rounded-full" />
            </div>
          ) : claims.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <List className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Select a date range and click Apply</p>
            </div>
          ) : (
            <ClaimsTable claims={filtered} mode="all" isSuperAdmin={isSuperAdmin} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AllClaims;
